import { useEffect, useState } from 'react'
import { StaticMaps, TilesetConfig } from '../../utils/rasterTools'
import { Polygon, MultiPolygon } from 'geojson'
import bbox from '@turf/bbox'
import chroma from 'chroma-js'
import CDL_METADATA from './fixtures/metadata.json'
import { sort, descend, prop } from 'ramda'

export const useGeometryRaster = (
  tileset: TilesetConfig,
  geometry: Polygon | MultiPolygon,
  legend: Record<string, Object> = CDL_METADATA.legend
) => {
  const [pngData, setPngData] = useState<string>()
  const [histogram, setHistogram] = useState<any>()
  const [totalPixels, setTotalPixels] = useState<number>()

  // @ts-ignore
  useEffect(async () => {
    if (!geometry) return
    const bounds = bbox(geometry)

    const rasterMap = new StaticMaps({
      width: 50,
      height: 50,
      zoomRange: {
        min: 6,
        max: 22,
      },
      tileLayers: [tileset],
    })

    rasterMap.addMask(geometry)

    const image = await rasterMap.render(bounds)

    image.getBase64('image/png', (err: Error, data: string) => setPngData(data))

    const counts: Record<string, number> = {}

    image.scan(
      0,
      0,
      image.bitmap.width,
      image.bitmap.height,
      function (x: number, y: number, idx: number) {
        const alpha = image.bitmap.data[idx + 3]
        if (alpha < 0.5) return

        const color = chroma(
          image.bitmap.data[idx],
          image.bitmap.data[idx + 1],
          image.bitmap.data[idx + 2]
        ).toString()
        if (!counts[color]) {
          counts[color] = 1
        } else {
          counts[color] += 1
        }
      }
    )

    const total = Object.values(counts).reduce((acc, item) => acc + item, 0)
    setTotalPixels(total)

    if (!legend) {
      setHistogram(counts)
      return
    }

    const withLegend: Array<{
      pixels: number
      portion: number
      mapColor: string
      paletteColor: string
    }> = []

    for (const [mapColor, count] of Object.entries(counts)) {
      Object.keys(legend).forEach(paletteColor => {
        if (chroma.distance(mapColor, paletteColor) < 5) {
          withLegend.push({
            mapColor,
            paletteColor,
            pixels: count,
            portion: count / total,
            ...legend[paletteColor],
          })
        }
      })
    }

    setHistogram(sort(descend(prop('pixels')), withLegend))
  }, [JSON.stringify(tileset), JSON.stringify(geometry)])

  return {
    pngData,
    histogram,
    totalPixels,
  }
}
